import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import OnboardingLayout from '../components/onboarding/OnboardingLayout';
import AccountForm from '../components/AccountForm';
import TransactionForm from '../components/TransactionForm';
import { useAuth } from '../contexts/AuthContext';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import { useTransactions } from '../hooks/useTransactions';
import {
  Compte,
  createCompte,
  getComptes,
  updateCompte,
} from '../lib/services/AccountService';
import { hasAnyTransaction } from '../lib/services/onboardingService';
import { Transaction } from '../lib/services/TransactionService';

type OnboardingStep = 'welcome' | 'accounts' | 'expense';

function useOnboardingComptesWithRetry() {
  const [comptes, setComptes] = useState<Compte[]>([]);
  const [loading, setLoading] = useState(true);
  const retryCountRef = useRef(0);

  const loadComptes = useCallback(async () => {
    const { data, error } = await getComptes();
    if (error) {
      console.warn('[Onboarding] loadComptes:', error);
      return [];
    }
    const rows = data ?? [];
    setComptes(rows);
    return rows;
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      await loadComptes();
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [loadComptes]);

  useEffect(() => {
    if (comptes.length > 0) return;

    retryCountRef.current = 0;
    const interval = setInterval(async () => {
      retryCountRef.current += 1;
      const rows = await loadComptes();
      if (rows.length > 0 || retryCountRef.current >= 5) {
        clearInterval(interval);
        setLoading(false);
      }
    }, 1500);

    return () => clearInterval(interval);
  }, [comptes.length, loadComptes]);

  return { comptes, loading, reload: loadComptes };
}

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { complete, loading: statusLoading, markComplete } = useOnboardingStatus();
  const { addTransaction } = useTransactions();
  const [step, setStep] = useState<OnboardingStep>('welcome');
  const [finishing, setFinishing] = useState(false);
  const [accountFormOpen, setAccountFormOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Compte | undefined>(undefined);
  const [transactionFormOpen, setTransactionFormOpen] = useState(false);

  const { comptes, loading: comptesLoading, reload: reloadComptes } =
    useOnboardingComptesWithRetry();

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!statusLoading && complete) {
      navigate('/', { replace: true });
    }
  }, [statusLoading, complete, navigate]);

  const finishOnboarding = useCallback(async () => {
    if (finishing) return;
    setFinishing(true);
    try {
      await markComplete();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('[Onboarding] finishOnboarding:', error);
      alert("Impossible de finaliser l'onboarding.");
      setFinishing(false);
    }
  }, [finishing, markComplete, navigate]);

  const checkForCompletedExpense = useCallback(async () => {
    const hasTransaction = await hasAnyTransaction();
    if (hasTransaction) {
      await finishOnboarding();
    }
  }, [finishOnboarding]);

  useEffect(() => {
    if (step === 'expense') {
      checkForCompletedExpense();
    }
  }, [step, checkForCompletedExpense]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);

  const openRenameFirst = () => {
    if (comptes.length === 0) {
      alert('Comptes en cours de création. Réessayez dans un instant ou ajoutez un compte manuellement.');
      return;
    }
    setEditingAccount(comptes[0]);
    setAccountFormOpen(true);
  };

  const openAddAccount = () => {
    setEditingAccount(undefined);
    setAccountFormOpen(true);
  };

  const openEditAccount = (account: Compte) => {
    setEditingAccount(account);
    setAccountFormOpen(true);
  };

  const handleAccountSubmit = async (account: Compte) => {
    if (editingAccount?.id) {
      const { data, error } = await updateCompte(editingAccount.id, account);
      if (error) return { success: false, error: error.message || 'Erreur' };
      await reloadComptes();
      return { success: true, data: data ?? undefined };
    }
    const { data, error } = await createCompte(account);
    if (error) return { success: false, error: error.message || 'Erreur' };
    await reloadComptes();
    return { success: true, data: data ?? undefined };
  };

  const handleTransactionSubmit = async (transaction: Transaction) => {
    const result = await addTransaction(transaction);
    if (result.success) {
      setTransactionFormOpen(false);
      await finishOnboarding();
    }
    return result;
  };

  if (!user || statusLoading) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  if (step === 'welcome') {
    return (
      <OnboardingLayout
        title="Bienvenue sur SuiviFinance"
        body="On ne vous demandera jamais vos identifiants bancaires. Vous seul décidez ce que vous ajoutez, et vous pouvez tout supprimer à tout moment."
        primaryLabel="Continuer"
        onPrimaryPress={() => setStep('accounts')}
      />
    );
  }

  if (step === 'accounts') {
    return (
      <>
        <OnboardingLayout
          title='Deux comptes ont déjà été créés pour vous : "Compte courant" et "Compte épargne"'
          body="Renommez-les si besoin, ou ajoutez-en un autre — ça prend 10 secondes."
          primaryLabel="Renommer mes comptes"
          onPrimaryPress={openRenameFirst}
          secondaryLabel="Ajouter un compte"
          onSecondaryPress={openAddAccount}
          tertiaryLabel="Passer pour l'instant"
          onTertiaryPress={() => setStep('expense')}
        >
          {comptesLoading && comptes.length === 0 ? (
            <div className="mt-8 flex items-center gap-3 text-dark-400">
              <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
              <span>Chargement de vos comptes…</span>
            </div>
          ) : (
            <div className="mt-8 space-y-3">
              {comptes.map((compte) => (
                <button
                  key={compte.id || compte.nom}
                  type="button"
                  onClick={() => openEditAccount(compte)}
                  className="w-full flex items-center gap-3 bg-dark-900 border border-dark-800 rounded-lg p-4 hover:border-dark-700 transition-colors text-left"
                >
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: compte.couleur }}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block text-white font-medium truncate">{compte.nom}</span>
                    <span className="block text-dark-400 text-sm">
                      {formatCurrency(Number(compte.solde ?? 0))}
                    </span>
                  </span>
                  <span className="text-dark-500 text-sm shrink-0">Modifier</span>
                </button>
              ))}
            </div>
          )}
        </OnboardingLayout>

        <AccountForm
          account={editingAccount}
          isOpen={accountFormOpen}
          onClose={() => {
            setAccountFormOpen(false);
            setEditingAccount(undefined);
          }}
          onSubmit={handleAccountSubmit}
          title={editingAccount?.id ? 'Modifier le compte' : 'Nouveau compte'}
        />
      </>
    );
  }

  return (
    <>
      <OnboardingLayout
        title="Ajoutez une dépense (facultatif)"
        body="Un café, des courses, peu importe — vous verrez solde et catégorie se mettre à jour. Vous pouvez aussi passer cette étape et le faire plus tard."
        primaryLabel="Ajouter une dépense"
        onPrimaryPress={() => setTransactionFormOpen(true)}
        primaryLoading={finishing}
        secondaryLabel="Scanner un reçu"
        onSecondaryPress={() => setTransactionFormOpen(true)}
        tertiaryLabel="Passer cette étape"
        onTertiaryPress={finishOnboarding}
      />

      <TransactionForm
        isOpen={transactionFormOpen}
        onClose={() => setTransactionFormOpen(false)}
        onSubmit={handleTransactionSubmit}
        title="Nouvelle dépense"
      />
    </>
  );
};

export default Onboarding;
